"use client";

import React, { useState, useEffect, useRef } from 'react';

interface StatItemProps {
  value: string;
  label: string;
  isVisible: boolean;
}

const StatItem: React.FC<StatItemProps> = ({ value, label, isVisible }) => {
  const [displayValue, setDisplayValue] = useState("0");
  const finalValue = parseInt(value.replace(/\D/g, ''), 10);
  const suffix = value.match(/[a-zA-Z+]+$/)?.[0] || ''; // Extract suffix like M+, k+
  const animationFrameRef = useRef<number | undefined>(undefined);
  const startTimeRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // Reset display value when it becomes invisible again (if needed)
    if (!isVisible) {
      setDisplayValue("0");
      startTimeRef.current = undefined; // Reset start time
      if (animationFrameRef.current !== undefined) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
      return;
    }
    
    const start = 0;
    const end = finalValue;
    const duration = 2000; // 2 seconds animation

    // Cubic ease-out function: starts fast, slows down towards the end
    const easeOutCubic = (t: number): number => 1 - Math.pow(1 - t, 3);

    const step = (timestamp: number) => {
      if (startTimeRef.current === undefined) {
        startTimeRef.current = timestamp;
      }

      const elapsedTime = timestamp - startTimeRef.current;
      const progress = Math.min(elapsedTime / duration, 1); // Linear progress: 0 to 1

      // Apply the easing function to the progress
      const easedProgress = easeOutCubic(progress);

      // Calculate currentValue based on eased progress
      const currentValue = Math.floor(start + (end - start) * easedProgress);

      setDisplayValue(`${currentValue}${suffix}`);

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(step);
      } else {
        // Ensure the final value is exactly set
        setDisplayValue(`${end}${suffix}`);
        startTimeRef.current = undefined; // Reset start time after completion
        animationFrameRef.current = undefined;
      }
    };

    // Reset start time and cancel previous frame if animation restarts due to visibility change
    startTimeRef.current = undefined;
    if (animationFrameRef.current !== undefined) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    // Start the animation
    animationFrameRef.current = requestAnimationFrame(step);

    // Cleanup function
    return () => {
      if (animationFrameRef.current !== undefined) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
      startTimeRef.current = undefined; // Reset start time on unmount or visibility loss
    };
  }, [isVisible, finalValue, suffix]); // Rerun effect if these change

  return (
    <div 
      className={`
        text-center transition-opacity duration-700 ease-out
        ${isVisible ? 'opacity-100' : 'opacity-0'}
      `}
    >
      {/* Removed translate-y animation for simplicity */}
      <div className="text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-3">
        {displayValue}
      </div>
      <div className="text-gray-300 text-base md:text-lg uppercase tracking-wider font-medium">
        {label}
      </div>
    </div>
  );
};

const StatsSection = () => {
  const stats = [
    { value: "8M+", label: "Total Hours Saved" },
    { value: "500k+", label: "Users" },
    { value: "80+", label: "Countries Served" }
  ];

  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.2 // Trigger when 20% of the element is visible
    };

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Optional: Keep observing if you want the animation to restart on re-scroll
          // If not, disconnect here:
          observer.unobserve(entry.target);
        }
        // Optional: Reset visibility if it scrolls out of view completely
        // else {
        //   setIsVisible(false);
        // }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={sectionRef} className="bg-[#121212] text-white pt-2 pb-12 md:pt-2 md:pb-15 overflow-x-hidden">
      <div className="container mx-auto px-4">
        <div className="text-center mb-14">
          <h2 className="text-3xl md:text-5xl font-bold mb-4 text-gray-100">
            Powering the Pulse of Productivity
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Empowering Meetings Worldwide
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 max-w-5xl mx-auto">
          {stats.map((stat, index) => (
            <div 
              key={index}
            >
              <StatItem 
                value={stat.value} 
                label={stat.label} 
                isVisible={isVisible} 
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StatsSection;
